import React, { useEffect, useState } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import LoginPage from "./components/LoginPage";
import { useDispatch } from "react-redux";
import PrivateRoute from "./components/PrivateRoute";
import { login } from "./features/auth/authSlice";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { responsiveFontSizes } from "@mui/material";
import ZsxqPage from "./components/Blog/ZsxqPage";
import SignupPage from "./components/SignupPage";
import PodcasterPodcastsPage from "./components/Podcaster/PodcasterPodcastsPage";
import { PersistGate } from "redux-persist/integration/react";
import { persistor } from "./store";
import CreatePodcastPage from "./components/Podcaster/CreatePodcastPage";
import MigratePodcastPage from "./components/Podcaster/MigratePodcastPage";
import PodcasterEpisodesPage from "./components/Podcaster/PodcasterEpisodesPage";
import EditorPage from "./components/Podcaster/EditorPage";
import VideoEditorPage from "./components/Podcaster/VideoEditorPage";
import UserManagementPage from "./components/Podcaster/UserManagementPage";
import TestAccountPage from "./components/Podcaster/TestAccountPage";
import ZsxqManagementPage from "./components/Podcaster/ZsxqManagementPage";
import SqlConsolePage from "./components/Podcaster/SqlConsolePage";
import AppManagementPage from "./components/Podcaster/AppManagementPage";
import PodcastBlog from "./components/Blog/PodcastBlogPage";
import Appbar from "./components/AppBar";
import Copyright from "./components/Copyright";
import HelpPage from "./components/Help/HelpPage";
import EpisodePage from "./components/Blog/EpisodePage";
import { getToken, getUser } from "./utils";
import { LoadingPage } from "./components/LoadingPage";
import VideoBlogsPage from "./components/Blog/VideoBlogs/VideoBlogsPage";
import { VideoPage } from "./components/Blog/VideoBlogs/VideoPage";
import ForgotPassword from "./components/ForgotPage";
import ResetPassword from "./components/ResetPasswordPage";
import CompleteSignup from "./components/CompleteSignupPage";
import SubscriptionPlansPage from "./components/Payment/SubscriptionPlansPage";
import SubscriptionSuccessPage from "./components/Payment/SubscriptionSuccessPage";
import SubscriptionCancelPage from "./components/Payment/SubscriptionCancelPage";
import SubscriptionManagePage from "./components/Payment/SubscriptionManagePage";
import PaymentSuccessPage from "./components/Payment/PaymentSuccessPage";
import PaymentCancelPage from "./components/Payment/PaymentCancelPage";
import MembershipIntroPage from "./components/MembershipIntroPage";
import AppsPage from "./components/AppsPage";
import PrivacyPolicyPage from "./components/PrivacyPolicyPage";
import { PlayerProvider } from "./player/PlayerContext";
import GlobalPlayerBar from "./player/GlobalPlayerBar";

// 品牌化 MUI 主题：让仍在使用 MUI 的页面（主要是 /podcaster 管理后台）
// 与 边角聊 设计系统（designSystem/tokens）的配色、字体、圆角保持一致。
const FONT_SANS =
  "'Source Sans 3','Noto Sans SC','PingFang SC','Hiragino Sans GB','Microsoft YaHei',sans-serif";
const FONT_DISPLAY =
  "'Bricolage Grotesque','Noto Sans SC','PingFang SC','Hiragino Sans GB','Microsoft YaHei',sans-serif";

let theme = createTheme({
  palette: {
    primary: { main: "#E8862F", dark: "#C96F1D", light: "#F49C4A", contrastText: "#1B1508" },
    secondary: { main: "#0C3CA8" },
    error: { main: "#BE3A30" },
    success: { main: "#1F7A50" },
    warning: { main: "#C96F1D" },
    info: { main: "#0C3CA8" },
    background: { default: "#F6F4EC", paper: "#FFFFFF" },
    text: { primary: "#131313", secondary: "#3E3D39" },
    divider: "#E5E2D6",
  },
  typography: {
    fontFamily: FONT_SANS,
    h1: { fontFamily: FONT_DISPLAY, fontWeight: 700 },
    h2: { fontFamily: FONT_DISPLAY, fontWeight: 700 },
    h3: { fontFamily: FONT_DISPLAY, fontWeight: 700 },
    h4: { fontFamily: FONT_DISPLAY, fontWeight: 700 },
    h5: { fontFamily: FONT_DISPLAY, fontWeight: 700 },
    h6: { fontFamily: FONT_DISPLAY, fontWeight: 700 },
    button: { textTransform: "none", fontWeight: 600 },
  },
  shape: { borderRadius: 10 },
  components: {
    MuiButton: {
      styleOverrides: { root: { borderRadius: 999 } },
    },
  },
});
// Apply responsive font sizes to the theme
theme = responsiveFontSizes(theme);

// 路由切换时回滚到页面顶部（React Router 默认保留上一页的滚动位置）
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const App = () => {
  const [isLoading, setIsLoading] = useState(true);
  const dispatch = useDispatch();

  useEffect(() => {
    const storedUser = getUser();
    const storedToken = getToken();
    if (storedUser && storedToken) {
      dispatch(login({ user: storedUser, token: storedToken }));
    }
    setIsLoading(false);
  }, [dispatch]);

  if (isLoading) {
    return <LoadingPage />;
  }

  return (
    <PersistGate loading={null} persistor={persistor}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <PlayerProvider>
          <ScrollToTop />
          <Appbar />
          <div className="lt-page">
            <Routes>
          <Route path="/" element={<PodcastBlog />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/forgot" element={<ForgotPassword />} />
          <Route path="/reset_password" element={<ResetPassword />} />
          <Route path="/complete_signup" element={<CompleteSignup />} />
          <Route path="/membership-intro" element={<MembershipIntroPage />} />
          <Route path="/help" element={<HelpPage />} />
          <Route path="/help/:topic" element={<HelpPage />} />
          <Route path="/apps" element={<AppsPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route
            path="/podcaster"
            element={
              <PrivateRoute>
                <PodcasterPodcastsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/podcaster/episodes"
            element={
              <PrivateRoute>
                <PodcasterEpisodesPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/podcaster/migrate_podcast"
            element={
              <PrivateRoute>
                <MigratePodcastPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/podcaster/create_podcast"
            element={
              <PrivateRoute>
                <CreatePodcastPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/podcaster/editor/:podcastId/:episodeId"
            element={
              <PrivateRoute>
                <EditorPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/podcaster/video-editor/:podcastId/:videoId"
            element={
              <PrivateRoute>
                <VideoEditorPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/podcaster/user_management"
            element={
              <PrivateRoute>
                <UserManagementPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/podcaster/test_account"
            element={
              <PrivateRoute>
                <TestAccountPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/podcaster/zsxq_management"
            element={
              <PrivateRoute>
                <ZsxqManagementPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/podcaster/app_management"
            element={
              <PrivateRoute>
                <AppManagementPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/podcaster/sql"
            element={
              <PrivateRoute>
                <SqlConsolePage />
              </PrivateRoute>
            }
          />
          <Route
            path="/zsxq"
            element={
              <PrivateRoute>
                <ZsxqPage />
              </PrivateRoute>
            }
          />
          <Route path="/episode/:id" element={<EpisodePage />} />
          <Route path="/videos" element={<VideoBlogsPage />} />
          <Route path="/video/:id" element={<VideoPage />} />
          <Route
            path="/subscription/plans"
            element={
              <PrivateRoute>
                <SubscriptionPlansPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/subscription/success"
            element={
              <PrivateRoute>
                <SubscriptionSuccessPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/subscription/cancel"
            element={<SubscriptionCancelPage />}
          />
          <Route
            path="/subscription/manage"
            element={
              <PrivateRoute>
                <SubscriptionManagePage />
              </PrivateRoute>
            }
          />
          <Route
            path="/payment/success"
            element={
              <PrivateRoute>
                <PaymentSuccessPage />
              </PrivateRoute>
            }
          />
              <Route path="/payment/cancel" element={<PaymentCancelPage />} />
            </Routes>
          </div>
          <Copyright />
          <GlobalPlayerBar />
        </PlayerProvider>
      </ThemeProvider>
    </PersistGate>
  );
};

export default App;
